import type { BonusCampaign } from '../api/fetchers/bonusPromotions/types/bonus';
import type { UserInfoType } from '../api/fetchers/userInfo/types';
import type { ChipValueType, FreeChipValueType } from '../store/slices/chips/types';
import { ChipSelectionOrigin, ChipTypes } from '../store/slices/chips/types';

import { getChipIndexFromWeight, selectMaxAffordableChip } from './chips';

export const selectFreeChip = (activeBonusCampaign: BonusCampaign): FreeChipValueType => {
  return {
    value: activeBonusCampaign.bonus.chipValue,
    weight: -1,
    type: ChipTypes.FreeChip,
    count: activeBonusCampaign.bonus.noOfchipsRemaining,
    maxLimit: activeBonusCampaign.bonus.maxBetLimit,
  };
};

export const selectCasinoChip = (
  chips: ChipValueType[],
  selectedChipWeight: number,
  userInfo: UserInfoType | null,
  totalBet: number
): ChipValueType => {
  const selectedChip = selectMaxAffordableChip(
    chips,
    chips[getChipIndexFromWeight(selectedChipWeight)] as ChipValueType,
    userInfo,
    totalBet
  );
  return selectedChip;
};

export const getNewUserFreeChip = (
  activeBonusCampaign: BonusCampaign,
  selectionOrigin: ChipSelectionOrigin = ChipSelectionOrigin.PlayerConfig
): FreeChipValueType => {
  const freeChipObject = selectFreeChip(activeBonusCampaign);
  return { ...freeChipObject, selectedBy: selectionOrigin };
};

export const getNewUserDefaultChip = (
  defaultChipWeight: number,
  chips: ChipValueType[],
  userInfo: UserInfoType | null,
  totalBet: number
): ChipValueType => {
  const defaultChip = chips[getChipIndexFromWeight(defaultChipWeight)] as ChipValueType;
  const selectedChip = selectCasinoChip(chips, defaultChip.weight, userInfo, totalBet);
  return { ...selectedChip, selectedBy: ChipSelectionOrigin.System };
};

export const getNewUserNoDefaultChip = (
  chips: ChipValueType[],
  userInfo: UserInfoType | null,
  totalBet: number
): ChipValueType => {
  const leastChip = chips[0] as ChipValueType;
  const selectedChip = selectCasinoChip(chips, leastChip.weight, userInfo, totalBet);
  return { ...selectedChip, selectedBy: ChipSelectionOrigin.System };
};

export const getUserCasinoChip = (selectedChipFromConfig: number, chips: ChipValueType[]): ChipValueType => {
  const selectedChipFromConfigFallback = selectedChipFromConfig <= chips.length ? selectedChipFromConfig : 0;
  const selectedChip = chips[selectedChipFromConfigFallback] as ChipValueType;
  return { ...selectedChip, selectedBy: ChipSelectionOrigin.PlayerConfig };
};

export const getFreeChipOnZeroBalance = (activeBonusCampaign: BonusCampaign): ChipValueType => {
  const freeChipObject = selectFreeChip(activeBonusCampaign);
  return { ...freeChipObject, selectedBy: ChipSelectionOrigin.System };
};
